// Registry of every closed set and status machine in core: the DB lanes generate named CHECK
// constraints from it [03 BR-DM-007] and the boundary check uses it [AC-KRN-04].
import { GATE_ID, OVERRIDE_CODE } from '../gates/registry.ts';
import { DOC_TYPE } from '../ports/numbering.ts';
import { FX_RATE_TYPE } from '../units/fx.ts';
import type { EnumDef } from './define.ts';
import type { Machine } from './machine.ts';
import * as fin from './machines-finance.ts';
import * as ops from './machines-ops.ts';
import * as plm from './machines-plm.ts';
import * as opsTypes from './types-ops.ts';
import * as platformTypes from './types-platform.ts';

function isEnumDef(v: unknown): v is EnumDef {
  return typeof v === 'object' && v !== null && 'values' in v && 'schema' in v && 'name' in v;
}
function isMachine(v: unknown): v is Machine {
  return typeof v === 'object' && v !== null && 'states' in v && 'transitions' in v;
}

/** 03 §18 document name → machine (the `Document` column; split rows get one entry each). */
export const MACHINES: Readonly<Record<string, Machine>> = Object.freeze({
  'product.style': plm.STYLE_LIFECYCLE,
  'product.style_spec': plm.STYLE_SPEC,
  'product.bom_revision': plm.BOM_REVISION,
  'product.sample': plm.SAMPLE,
  'product.dev_approval': plm.DEV_APPROVAL,
  'costing.cost_sheet_version': plm.COST_SHEET_VERSION,
  'costing.actual_cost': plm.ACTUAL_COST,
  'costing.landed_cost_charge': plm.LANDED_COST_CHARGE,
  'materials.lot': plm.LOT_QUALITY,
  'materials.roll': plm.ROLL,
  'materials.quality_hold': plm.QUALITY_HOLD,
  'materials.fabric_test': plm.FABRIC_TEST,
  'sales.programme': ops.PROGRAMME,
  'sales.sales_order': ops.SALES_ORDER,
  'sales.po_import_document': ops.PO_IMPORT_DOCUMENT,
  'sales.customer_return': ops.CUSTOMER_RETURN,
  'purchasing.purchase_order': ops.PURCHASE_ORDER,
  'purchasing.inbound_shipment': ops.INBOUND_SHIPMENT,
  'purchasing.goods_receipt': ops.GOODS_RECEIPT,
  'inventory.stock_adjustment': ops.STOCK_ADJUSTMENT,
  'inventory.warehouse_document': ops.WAREHOUSE_DOCUMENT,
  'inventory.stock_reservation': ops.STOCK_RESERVATION,
  'production.cutting_slip': ops.CUTTING_SLIP,
  'production.cut_report': ops.CUT_REPORT,
  'production.fg_receipt': ops.FG_RECEIPT,
  'production.sewing_allocation': ops.SEWING_ALLOCATION,
  'dispatch.shipment': ops.SHIPMENT,
  'dispatch.label_run': ops.LABEL_RUN,
  'dispatch.credit_debit_note': ops.CREDIT_DEBIT_NOTE,
  'planning.requirement_run': ops.REQUIREMENT_RUN,
  'channels.d2c_order': ops.D2C_ORDER,
  'channels.shopify_variant_map': ops.SHOPIFY_VARIANT_MAP,
  'finance.fiscal_period': fin.FISCAL_PERIOD,
  'finance.journal_entry': fin.JOURNAL_ENTRY,
  'finance.ap_document': fin.AP_DOCUMENT,
  'finance.ar_document': fin.AR_DOCUMENT,
  'finance.customer_deduction': fin.CUSTOMER_DEDUCTION,
  'finance.payment': fin.PAYMENT,
  'finance.payment_run': fin.PAYMENT_RUN,
  'finance.bank_reconciliation': fin.BANK_RECONCILIATION,
  'finance.fx_revaluation': fin.FX_REVALUATION,
  'finance.sage_export_batch': fin.SAGE_EXPORT_BATCH,
  'core.approval_request': fin.APPROVAL_REQUEST,
  'core.print_job': fin.PRINT_JOB,
  'core.job_run': fin.JOB_RUN,
});

const typeEnums = (
  [...Object.values(platformTypes), ...Object.values(opsTypes)] as unknown[]
).filter(isEnumDef);
const machineEnums = [plm, ops, fin].flatMap((mod) =>
  (Object.values(mod) as unknown[]).filter(isMachine).map((m) => m.states),
);

/** Every closed set, unique by name (`schema.table.column`). */
export const ENUM_CATALOGUE: readonly EnumDef[] = Object.freeze(
  [...typeEnums, ...machineEnums, FX_RATE_TYPE, OVERRIDE_CODE, GATE_ID, DOC_TYPE].filter(
    (e, i, all) => all.findIndex((x) => x.name === e.name) === i,
  ),
);

export function enumByName(name: string): EnumDef | undefined {
  return ENUM_CATALOGUE.find((e) => e.name === name);
}
